import React, { useState, useEffect, useMemo, useRef } from 'react';
import pt from 'prop-types';
import {
  resolveThemeSettings,
  getThemeSettingsFromProps,
} from '@swell/apps-sdk';
import isEqual from 'lodash/isEqual';

import { InlineEditableProvider } from 'components/storefront/editor/easyblocks-page/components/inline-editable';
import { injectGlobalCSS } from 'components/storefront/editor/global-css-widget';
import useAsync from 'components/storefront/editor/utils/use-async';

import { RootContext } from './index';
import parse from './parser';

/** @typedef {import('@swell/apps-sdk').SwellTheme} SwellTheme */
/** @typedef {import('components/storefront/editor/easyblocks-page/asset-loader').default} AssetLoader */

/**
 * @typedef {object} RootProps
 * @property {SwellTheme} theme
 * @property {AssetLoader} assetLoader
 */

const LIQUID_ASSET_REGEX = /assets\/.*\..*\.liquid$/;

/** @param {RootProps} props */
function Root(props) {
  const {
    Root,
    pageProps,
    layoutProps,
    pageContent,
    editorSchema,
    fetchThemeConfigs,
    createThemeConfig,
    updateThemeConfig,
    assetLoader,
    branchId,
    themeId,
    theme,
  } = props;

  const [template, setTemplate] = useState('');
  const [pageContentString, setPageContentString] = useState(null);
  const [Content, setContent] = useState(() => <div />);
  const settingsRef = useRef(null);

  const settings = useMemo(() => {
    const settingProps = Object.assign(
      {},
      props.pageProps.settings || {},
      getThemeSettingsFromProps(props, editorSchema),
    );

    const settings = resolveThemeSettings(theme, settingProps, editorSchema);

    settingsRef.current = isEqual(settingsRef.current, settings)
      ? settingsRef.current
      : settings;

    return settingsRef.current;
  }, [props, editorSchema, theme]);

  const { loading: compiling } = useAsync(
    (signal) => {
      theme.setGlobals({ settings });

      return compileLiquidAssets({
        theme,
        themeId,
        branchId,
        fetchThemeConfigs,
        createThemeConfig,
        updateThemeConfig,
        signal,
      });
    },
    [
      settings,
      theme,
      themeId,
      branchId,
      fetchThemeConfigs,
      createThemeConfig,
      updateThemeConfig,
    ],
  );

  useEffect(() => {
    if (compiling) {
      return;
    }

    console.log('render layout', { settings: theme.globals?.settings });

    if (theme.globals?.settings?.custom_css) {
      let customCss = theme.globals.settings.custom_css;
      if (Array.isArray(customCss)) {
        customCss = customCss.join('\n');
      }

      injectGlobalCSS(customCss);
    }

    let canceled = false;

    theme
      .renderLayout(pageContent.layout || '', {
        ...layoutProps,
        content_for_header: theme.getContentForHeader(),
        content_for_layout: '<content-for-layout></content-for-layout>',
      })
      .then(async (template) => {
        if (canceled) return;

        // Render theme page directly when it's a raw liquid file
        const pageContentString =
          typeof pageContent === 'string'
            ? await theme.renderTemplateString(pageContent, pageProps)
            : null;

        if (canceled) return;

        setTemplate(template);
        setPageContentString(pageContentString);
      });

    return () => {
      canceled = true;
    };
  }, [compiling, layoutProps, pageContent, pageProps, theme, theme.globals]);

  useEffect(() => {
    if (!template) {
      return;
    }

    const { Content } = parse(theme, assetLoader, template, pageContentString);

    setContent(Content);
  }, [template, pageContentString, theme, assetLoader]);

  const RootComponent = Root.type;

  return (
    <RootComponent {...Root.props} className="easyblocks-page--root">
      <RootContext.Provider value={props}>
        <InlineEditableProvider theme={theme}>{Content}</InlineEditableProvider>
      </RootContext.Provider>
    </RootComponent>
  );
}

Root.propTypes = {
  Root: pt.element,
  theme: pt.object,
  pageProps: pt.object,
  layoutProps: pt.object,
  pageContent: pt.oneOfType([pt.string, pt.object]),
  editorSchema: pt.array,
  assetLoader: pt.object,
  branchId: pt.string,
  themeId: pt.string,

  fetchThemeConfigs: pt.func,
  createThemeConfig: pt.func,
  updateThemeConfig: pt.func,
};

function getRootComponent(props, theme, assetLoader) {
  const {
    layoutProps,
    pageProps,
    pageContent,
    fetchThemeConfigs,
    createThemeConfig,
    updateThemeConfig,
    branchId,
    themeId,
  } = props;

  const editorSchema = pageProps.configs?.editor?.settings;

  function RootWrapper(rootProps) {
    return (
      <Root
        themeId={themeId}
        branchId={branchId}
        fetchThemeConfigs={fetchThemeConfigs}
        createThemeConfig={createThemeConfig}
        updateThemeConfig={updateThemeConfig}
        layoutProps={layoutProps}
        pageProps={pageProps}
        pageContent={pageContent}
        editorSchema={editorSchema}
        assetLoader={assetLoader}
        theme={theme}
        {...rootProps}
      />
    );
  }

  return React.memo(RootWrapper);
}

/**
 * @param {object} params
 * @param {SwellTheme} params.theme
 * @param {string} params.themeId
 * @param {string | null} params.branchId
 * @param {Function} params.fetchThemeConfig
 * @param {Function} params.createThemeConfig
 * @param {Function} params.updateThemeConfig
 * @param {AbortSignal} params.signal
 * @returns {Promise<void>}
 */
async function compileLiquidAssets({
  theme,
  themeId,
  branchId,
  fetchThemeConfigs,
  createThemeConfig,
  updateThemeConfig,
  signal,
}) {
  const settingsConfig = await theme.getThemeConfig(theme.themeSettingFilePath);

  if (!settingsConfig || signal.aborted) {
    return;
  }

  const liquidAssets = Array.from(theme.themeConfigs.values()).filter(
    (config) => LIQUID_ASSET_REGEX.test(config.file_path),
  );

  if (liquidAssets.length <= 0) {
    return;
  }

  const { results } = await Promise.all(
    liquidAssets.map(async (sourceConfig) => {
      const assetConfig = await theme.getThemeConfig(
        sourceConfig.file_path.replace('.liquid', ''),
      );

      if (signal.aborted) return;

      if (assetConfig) {
        if (assetConfig.settings_hash !== settingsConfig.hash) {
          const compiled = await theme.renderTemplate(sourceConfig);

          if (signal.aborted) return;

          return updateThemeConfig(assetConfig.id, {
            file: { data: compiled },
            settings_hash: settingsConfig.hash,
          });
        }
      } else {
        const compiled = await theme.renderTemplate(sourceConfig);

        if (signal.aborted) return;

        return createThemeConfig({
          parent_id: themeId,
          branch_id: branchId,
          preview: true,
          type: 'theme',
          name: sourceConfig.name,
          file: { data: compiled },
          file_path: sourceConfig.file_path.replace('.liquid', ''),
          settings_hash: settingsConfig.hash,
        });
      }
    }),
  ).then((configs) => {
    configs = configs.filter(Boolean);

    if (configs.length <= 0 || signal.aborted) {
      return { results: [] };
    }

    return fetchThemeConfigs(themeId, branchId, {
      id: { $in: configs.map((config) => config.id) },
    });
  });

  if (signal.aborted) {
    return;
  }

  for (const config of results) {
    theme.themeConfigs.set(config.file_path, config);
  }
}

export default getRootComponent;
